<script>
    import { onMount } from 'svelte';
    import { imageStore, editStore } from '../stores';
    import { startAutotrainFinetuning, checkAutotrainStatus } from '../utils/apiUtils';
    
    // 파인튜닝 상태 관리
    let uploadedImages = [];
    let projectName = "";
    let description = "";
    let isUploading = false;
    let isTraining = false;
    let trainingProgress = 0;
    let trainingStatus = "";
    let modelId = "";
    let errorMessage = "";
    let maxImages = 50;
    let requiredImages = 10;
    let credits = 100;
    let costPerTraining = 50;
    
    // 파일 입력 참조
    let fileInput;
    
    // 이미지 업로드 처리
    function handleFileSelect(event) {
      const files = event.target.files;
      if (!files || files.length === 0) return;
      
      if (uploadedImages.length + files.length > maxImages) {
        errorMessage = `최대 ${maxImages}개의 이미지만 업로드할 수 있습니다.`;
        return;
      }
      
      errorMessage = "";
      
      Array.from(files).forEach(file => {
        if (!file.type.startsWith('image/')) {
          errorMessage = "이미지 파일만 업로드할 수 있습니다.";
          return;
        }
        
        const reader = new FileReader();
        reader.onload = (e) => {
          uploadedImages = [...uploadedImages, {
            id: Date.now() + Math.random().toString(36).substring(2, 9),
            name: file.name,
            size: file.size,
            type: file.type,
            dataUrl: e.target.result,
            file: file
          }];
        };
        reader.readAsDataURL(file);
      });
      
      // 파일 입력 초기화 (같은 파일 다시 선택 가능하도록)
      fileInput.value = "";
    }
    
    // 이미지 제거
    function removeImage(id) {
      uploadedImages = uploadedImages.filter(img => img.id !== id);
    }
    
    // 모든 이미지 제거
    function clearAllImages() {
      uploadedImages = [];
      projectName = "";
      description = "";
      errorMessage = "";
    }
    
    // 파인튜닝 시작
    async function startFinetuning() {
      if (uploadedImages.length < requiredImages) {
        errorMessage = `최소 ${requiredImages}개의 이미지가 필요합니다.`;
        return;
      }
      
      if (!projectName.trim()) {
        errorMessage = "프로젝트 이름을 입력해주세요.";
        return;
      }
      
      if (credits < costPerTraining) {
        errorMessage = `파인튜닝을 위한 크레딧이 부족합니다. 필요: ${costPerTraining}, 보유: ${credits}`;
        return;
      }
      
      errorMessage = "";
      isUploading = true;
      trainingStatus = "이미지 업로드 중...";
      
      try {
        // 이미지 업로드 시뮬레이션
        await simulateUpload();
        
        // 파인튜닝 시작 시뮬레이션
        isUploading = false;
        isTraining = true;
        trainingStatus = "모델 학습 중...";
        
        // 실제 API 호출 (현재는 시뮬레이션)
        const imageDataUrls = uploadedImages.map(img => img.dataUrl);
        const result = await startAutotrainFinetuning(projectName, description, imageDataUrls);
        modelId = result.model_id;
        
        // 학습 진행 시뮬레이션
        await simulateTraining();
        
        // 학습 완료
        trainingStatus = "학습 완료!";
        
        // 크레딧 차감
        credits -= costPerTraining;
        
        // 결과 메시지 표시
        setTimeout(() => {
          alert(`모델 파인튜닝이 완료되었습니다!\n모델 ID: ${modelId}\n이제 이 모델을 사용하여 더 정확한 결과를 얻을 수 있습니다.`);
          isTraining = false;
          trainingProgress = 0;
        }, 1000);
        
      } catch (error) {
        console.error("파인튜닝 오류:", error);
        errorMessage = "파인튜닝 중 오류가 발생했습니다. 다시 시도해주세요.";
        isUploading = false;
        isTraining = false;
      }
    }
    
    // 업로드 시뮬레이션
    function simulateUpload() {
      return new Promise(resolve => {
        let progress = 0;
        const interval = setInterval(() => {
          progress += 10;
          trainingProgress = progress;
          
          if (progress >= 100) {
            clearInterval(interval);
            resolve();
          }
        }, 300);
      });
    }
    
    // 학습 시뮬레이션
    function simulateTraining() {
      return new Promise(resolve => {
        let progress = 0;
        trainingProgress = 0;
        
        const interval = setInterval(() => {
          progress += 5;
          trainingProgress = progress;
          
          if (progress < 30) {
            trainingStatus = "데이터 전처리 중...";
          } else if (progress < 60) {
            trainingStatus = "모델 학습 중...";
          } else if (progress < 90) {
            trainingStatus = "모델 최적화 중...";
          } else {
            trainingStatus = "모델 저장 중...";
          }
          
          if (progress >= 100) {
            clearInterval(interval);
            resolve();
          }
        }, 500);
      });
    }
    
    // 현재 이미지 추가
    function addCurrentImage() {
      if (!$imageStore.resultImage && !$imageStore.uploadedImageUrl) {
        errorMessage = "현재 편집 중인 이미지가 없습니다.";
        return;
      }
      
      if (uploadedImages.length >= maxImages) {
        errorMessage = `최대 ${maxImages}개의 이미지만 업로드할 수 있습니다.`;
        return;
      }
      
      const imageUrl = $imageStore.resultImage || $imageStore.uploadedImageUrl;
      
      uploadedImages = [...uploadedImages, {
        id: Date.now() + Math.random().toString(36).substring(2, 9),
        name: "현재 이미지",
        size: 0,
        type: "image/png",
        dataUrl: imageUrl
      }];
      
      errorMessage = "";
    }
    
    // 컴포넌트 마운트 시 초기화
    onMount(() => {
      // 여기에 필요한 초기화 코드 추가
    });
  </script>
  
  <div class="autotrain-container">
    <h2>내 반려견 모델 파인튜닝</h2>
    
    <div class="info-section">
      <p>
        내 반려견 이미지로 AI 모델을 학습시켜 더 정확한 결과를 얻을 수 있습니다.
        최소 {requiredImages}장, 최대 {maxImages}장의 이미지를 업로드하세요.
      </p>
      <p class="credits-info">
        보유 크레딧: <span class="credits">{credits}</span> | 
        파인튜닝 비용: <span class="cost">{costPerTraining}</span> 크레딧
      </p>
    </div>
    
    {#if errorMessage}
      <div class="error-message">
        {errorMessage}
      </div>
    {/if}
    
    <div class="project-info">
      <div class="input-group">
        <label for="project-name">프로젝트 이름</label>
        <input 
          type="text" 
          id="project-name" 
          bind:value={projectName} 
          placeholder="예: 내 강아지 모델"
          disabled={isUploading || isTraining}
        />
      </div>
      
      <div class="input-group">
        <label for="description">설명 (선택사항)</label>
        <textarea 
          id="description" 
          bind:value={description} 
          placeholder="모델에 대한 설명을 입력하세요."
          disabled={isUploading || isTraining}
        ></textarea>
      </div>
    </div>
    
    <div class="upload-section">
      <div class="upload-actions">
        <button 
          class="upload-btn" 
          on:click={() => fileInput.click()}
          disabled={isUploading || isTraining || uploadedImages.length >= maxImages}
        >
          이미지 업로드
        </button>
        
        <button 
          class="add-current-btn" 
          on:click={addCurrentImage}
          disabled={isUploading || isTraining || uploadedImages.length >= maxImages}
        >
          현재 이미지 추가
        </button>
        
        <button 
          class="clear-btn" 
          on:click={clearAllImages}
          disabled={isUploading || isTraining || uploadedImages.length === 0}
        >
          모두 지우기
        </button>
        
        <input 
          type="file" 
          bind:this={fileInput} 
          on:change={handleFileSelect} 
          accept="image/*" 
          multiple 
          style="display: none;"
        />
      </div>
      
      <div class="upload-status">
        <span class="upload-count">
          {uploadedImages.length}/{maxImages} 이미지 업로드됨
          {#if uploadedImages.length < requiredImages}
            <span class="required-notice">
              (최소 {requiredImages}장 필요)
            </span>
          {/if}
        </span>
      </div>
    </div>
    
    {#if uploadedImages.length > 0}
      <div class="image-grid">
        {#each uploadedImages as image (image.id)}
          <div class="image-item">
            <img src={image.dataUrl} alt={image.name} />
            <div class="image-info">
              <span class="image-name">{image.name}</span>
              {#if !isUploading && !isTraining}
                <button class="remove-btn" on:click={() => removeImage(image.id)}>
                  ✕
                </button>
              {/if}
            </div>
          </div>
        {/each}
      </div>
    {:else}
      <div class="empty-state">
        <p>파인튜닝을 위한 이미지를 업로드해주세요.</p>
        <p>다양한 각도와 배경의 반려견 이미지를 사용하면 더 좋은 결과를 얻을 수 있습니다.</p>
      </div>
    {/if}
    
    {#if isUploading || isTraining}
      <div class="progress-container">
        <div class="progress-status">
          {trainingStatus}
        </div>
        <div class="progress-bar">
          <div class="progress-fill" style="width: {trainingProgress}%"></div>
        </div>
        <div class="progress-percentage">
          {trainingProgress}%
        </div>
      </div>
    {/if}
    
    <div class="action-buttons">
      <button 
        class="start-btn" 
        on:click={startFinetuning}
        disabled={isUploading || isTraining || uploadedImages.length < requiredImages || !projectName.trim()}
      >
        파인튜닝 시작
      </button>
    </div>
    
    {#if modelId}
      <div class="result-section">
        <h3>파인튜닝 완료!</h3>
        <p>모델 ID: <span class="model-id">{modelId}</span></p>
        <p>이제 이 모델을 사용하여 더 정확한 결과를 얻을 수 있습니다.</p>
      </div>
    {/if}
  </div>
  
  <style>
    .autotrain-container {
      display: flex;
      flex-direction: column;
      gap: 1.5rem;
      padding: 1.5rem;
      max-width: 100%;
    }
    
    h2 {
      color: #FF6382;
      margin: 0;
      font-size: 1.5rem;
    }
    
    .info-section {
      padding: 1rem;
      border-radius: 8px;
      margin-bottom: 1rem;
    }

    :global(.dark) .info-section {
      background-color: rgba(255, 255, 255, 0.05);
    }

    :global(.light) .info-section {
      background-color: rgba(0, 0, 0, 0.05);
    }
    
    .info-section p {
      margin: 0.5rem 0;
      font-size: 0.9rem;
    }
    
    .credits-info {
      font-weight: 500;
      margin-top: 0.5rem;
    }
    
    .credits {
      color: #4CAF50;
      font-weight: bold;
    }
    
    .cost {
      color: #FF6382;
      font-weight: bold;
    }
    
    .error-message {
      background-color: rgba(244, 67, 54, 0.1);
      color: #f44336;
      padding: 0.8rem;
      border-radius: 8px;
      margin-bottom: 1rem;
      font-size: 0.9rem;
    }
    
    .project-info {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      margin-bottom: 1rem;
    }
    
    .input-group {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }
    
    label {
      font-size: 0.9rem;
      font-weight: 500;
    }
    
    input, textarea {
      padding: 0.8rem;
      border-radius: 8px;
      color: inherit;
      font-size: 0.9rem;
      resize: vertical;
    }

    :global(.dark) input,
    :global(.dark) textarea {
      border: 1px solid rgba(255, 255, 255, 0.1);
      background-color: rgba(255, 255, 255, 0.05);
    }

    :global(.light) input,
    :global(.light) textarea {
      border: 1px solid rgba(0, 0, 0, 0.2);
      background-color: rgba(255, 255, 255, 0.9);
    }
    
    textarea {
      min-height: 80px;
    }
    
    .upload-section {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      margin-bottom: 1rem;
    }
    
    .upload-actions {
      display: flex;
      gap: 0.8rem;
      flex-wrap: wrap;
    }
    
    .upload-btn, .add-current-btn, .clear-btn {
      padding: 0.8rem 1.2rem;
      border-radius: 8px;
      border: none;
      font-size: 0.9rem;
      cursor: pointer;
      transition: all 0.3s ease;
    }
    
    .upload-btn {
      background: linear-gradient(90deg, #FF6382, #7C4DFF);
      color: white;
    }
    
    .add-current-btn {
      background-color: rgba(124, 77, 255, 0.2);
      color: #7C4DFF;
    }
    
    .clear-btn {
      background-color: rgba(255, 255, 255, 0.1);
      color: inherit;
    }
    
    .upload-btn:hover, .add-current-btn:hover, .clear-btn:hover {
      transform: translateY(-2px);
      box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    }
    
    .upload-btn:disabled, .add-current-btn:disabled, .clear-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
    
    .upload-status {
      font-size: 0.9rem;
    }

    :global(.dark) .upload-status {
      color: rgba(255, 255, 255, 0.7);
    }

    :global(.light) .upload-status {
      color: rgba(0, 0, 0, 0.7);
    }
    
    .required-notice {
      color: #FF6382;
      font-weight: 500;
    }
    
    .image-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
      gap: 1rem;
      margin-bottom: 1rem;
    }
    
    .image-item {
      position: relative;
      border-radius: 8px;
      overflow: hidden;
      aspect-ratio: 1;
    }

    :global(.dark) .image-item {
      background-color: rgba(255, 255, 255, 0.05);
    }

    :global(.light) .image-item {
      background-color: rgba(0, 0, 0, 0.05);
    }
    
    .image-item img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    
    .image-info {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      padding: 0.5rem;
      background-color: rgba(0, 0, 0, 0.7);
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    
    .image-name {
      font-size: 0.8rem;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 70%;
    }
    
    .remove-btn {
      background: none;
      border: none;
      color: #f44336;
      cursor: pointer;
      font-size: 1rem;
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
    }
    
    .empty-state {
      text-align: center;
      padding: 2rem;
      border-radius: 8px;
      margin-bottom: 1rem;
    }

    :global(.dark) .empty-state {
      background-color: rgba(255, 255, 255, 0.05);
    }

    :global(.light) .empty-state {
      background-color: rgba(0, 0, 0, 0.05);
    }
    
    .empty-state p {
      margin: 0.5rem 0;
    }

    :global(.dark) .empty-state p {
      color: rgba(255, 255, 255, 0.7);
    }

    :global(.light) .empty-state p {
      color: rgba(0, 0, 0, 0.7);
    }
    
    .progress-container {
      margin-bottom: 1rem;
    }
    
    .progress-status {
      font-size: 0.9rem;
      margin-bottom: 0.5rem;
    }
    
    .progress-bar {
      height: 8px;
      border-radius: 4px;
      overflow: hidden;
      margin-bottom: 0.5rem;
    }

    :global(.dark) .progress-bar {
      background-color: rgba(255, 255, 255, 0.1);
    }

    :global(.light) .progress-bar {
      background-color: rgba(0, 0, 0, 0.1);
    }
    
    .progress-fill {
      height: 100%;
      background: linear-gradient(90deg, #FF6382, #7C4DFF);
      transition: width 0.3s ease;
    }
    
    .progress-percentage {
      font-size: 0.8rem;
      text-align: right;
    }
    
    .action-buttons {
      display: flex;
      justify-content: center;
      margin-top: 1rem;
    }
    
    .start-btn {
      padding: 1rem 2rem;
      border-radius: 8px;
      border: none;
      background: linear-gradient(90deg, #FF6382, #7C4DFF);
      color: white;
      font-size: 1rem;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.3s ease;
    }
    
    .start-btn:hover {
      transform: translateY(-3px);
      box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
    }
    
    .start-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
    
    .result-section {
      margin-top: 1rem;
      padding: 1rem;
      background-color: rgba(76, 175, 80, 0.1);
      border-radius: 8px;
      text-align: center;
    }
    
    .result-section h3 {
      color: #4CAF50;
      margin: 0 0 0.5rem 0;
    }
    
    .model-id {
      font-family: 'Pretendard Medium';
      padding: 0.2rem 0.5rem;
      border-radius: 4px;
    }

    :global(.dark) .model-id {
      background-color: rgba(255, 255, 255, 0.1);
    }

    :global(.light) .model-id {
      background-color: rgba(0, 0, 0, 0.1);
    }
    
    @media (max-width: 768px) {
      .image-grid {
        grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
      }
      
      .upload-actions {
        flex-direction: column;
      }
      
      .upload-btn, .add-current-btn, .clear-btn {
        width: 100%;
      }
    }
  </style>
  